import React, { useEffect, useState } from "react";
import {
  closeCommandCenter,
  getCommandCenterState,
  setCommandCenterState,
  subscribeCommandCenter,
  toggleCommandCenter,
} from "./CommandCenter";
import { pushNotification } from "./NotificationManager";
import { openSearch } from "./SearchManager";
import { openSystemStatus } from "./SystemStatusManager";
import {
  handleBack,
  registerBackHandler,
  unregisterBackHandler,
} from "./BackManager";

// NapflixOS Command Center v8
// Passive OS quick menu. It owns only its explicit shortcut and open-menu keys.
const notifyComingSoon = (message) => {
  pushNotification({
    type: "command-center",
    title: "Command Center",
    message,
    level: "info",
  });
};

const activateCommand = (command) => {
  if (!command) return;

  switch (command.id) {
    case "tv-guide":
      window.dispatchEvent(new CustomEvent("pj:setPage", { detail: "tv" }));
      closeCommandCenter();
      break;
    case "ambient":
      notifyComingSoon("Ambient controls coming soon.");
      closeCommandCenter();
      break;
    case "system-status":
      closeCommandCenter();
      openSystemStatus();
      break;
    case "search":
      closeCommandCenter();
      openSearch();
      break;
    case "restart":
      notifyComingSoon("Restart command armed later.");
      closeCommandCenter();
      break;
    case "close":
    default:
      closeCommandCenter();
      break;
  }
};

export default function CommandCenterOverlay() {
  const [state, setState] = useState(getCommandCenterState());

  useEffect(() => {
    setState(getCommandCenterState());
    return subscribeCommandCenter(setState);
  }, []);

  useEffect(() => {
    registerBackHandler({
      id: "command-center",
      priority: 90,
      canHandle: () => getCommandCenterState().open,
      handle: () => {
        closeCommandCenter();
        return true;
      },
      label: "Command Center",
    });

    return () => unregisterBackHandler("command-center");
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      const shortcut =
        event.key === "F1" ||
        ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k");

      if (shortcut) {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        toggleCommandCenter();
        return;
      }

      const currentState = getCommandCenterState();
      if (!currentState.open) return;

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();

        const dir = event.key === "ArrowUp" ? -1 : 1;
        const count = currentState.commands.length;
        const selectedIndex =
          (currentState.selectedIndex + dir + count) % count;

        setCommandCenterState({ selectedIndex });
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        activateCommand(currentState.commands[currentState.selectedIndex]);
        return;
      }

      if (event.key === "Escape" || event.key === "Backspace") {
        event.preventDefault();
        event.stopImmediatePropagation?.() || event.stopPropagation();
        handleBack("keyboard");
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  if (!state.open) return null;

  return (
    <div
      aria-hidden="true"
      onMouseDown={(event) => event.preventDefault()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        background:
          "radial-gradient(circle at center, rgba(24, 32, 52, 0.38), rgba(0,0,0,0.62) 70%)",
      }}
    >
      <style>{`
        @keyframes napflixCommandCenterIn {
          from { opacity: 0; transform: translate3d(0, 12px, 0) scale(0.98); }
          to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
      `}</style>

      <div
        style={{
          width: 420,
          borderRadius: 18,
          border: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(8, 12, 22, 0.86)",
          boxShadow:
            "0 28px 80px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.12)",
          backdropFilter: "blur(18px) saturate(1.25)",
          color: "rgba(255,255,255,0.92)",
          overflow: "hidden",
          animation: "napflixCommandCenterIn 180ms ease both",
        }}
      >
        <div
          style={{
            padding: "18px 22px 12px",
            fontFamily: "'Outfit', sans-serif",
            fontSize: 11,
            letterSpacing: 2.4,
            textTransform: "uppercase",
            color: "rgba(229, 199, 93, 0.9)",
          }}
        >
          Command Center
        </div>

        <div style={{ padding: "0 10px 12px" }}>
          {state.commands.map((command, index) => {
            const selected = index === state.selectedIndex;

            return (
              <div
                key={command.id}
                onClick={() => activateCommand(command)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 48,
                  padding: "0 14px",
                  borderRadius: 12,
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: 15,
                  background: selected
                    ? "rgba(229,160,13,0.18)"
                    : "transparent",
                  color: selected
                    ? "rgba(255,255,255,0.98)"
                    : "rgba(255,255,255,0.72)",
                  boxShadow: selected
                    ? "inset 0 0 0 1px rgba(229,160,13,0.42), 0 0 24px rgba(229,160,13,0.12)"
                    : "none",
                  cursor: "default",
                }}
              >
                {command.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
